import { ImageFit, ImageFormat, imageHelper, ImageSize } from '../../utils';

export interface IFocusModel {
  /** CMS BeoItem slug, e.g. `beogram-400x`. */
  slug: string;
  /** Route prefix and id, matching `/:prefix/:id`. */
  prefix: string;
  id: string;
  path: string;
  name: string;
  image: string;
  imageAlt: string;
  /** One or two sentences for model cards. */
  summary: string;
  /** Opening paragraph of the model hub. */
  intro: string;
  /** Typical faults, each covered by a repair or a part on the site. */
  commonIssues: string[];
  /** Slugs of the posts listed first on the model page, in this order: the work every unit gets. */
  leadWork: string[];
}

const cmsImage = (filename: string): string => imageHelper(filename, ImageFormat.jpg, ImageFit.cover, ImageSize.headerImage);

/**
 * The models the site focuses on. Pages for other models stay live and are
 * reachable from the Parts catalogue.
 */
export const focusModels: IFocusModel[] = [
  {
    slug: 'beogram-400x',
    prefix: 'beogram',
    id: '400x',
    path: '/beogram/400x',
    name: 'BeoGram 400X',
    image: cmsImage('6e738692-4c2d-447b-b39b-8d310f105819.jpg'),
    imageAlt: 'A Bang & Olufsen BeoGram 4000 with the platter exposed during restoration',
    summary: 'The BeoGram 4000, 4002 and 4004. Relays, motors, tracking sensors and more, with open-source parts of my own.',
    intro: `The BeoGram 4000, 4002 and 4004 share the same tangential tonearm and sledge design. Fifty years on, the
      same parts tend to wear out, and almost all of them can be repaired or replaced.`,
    commonIssues: [
      'Sticking or failing relays',
      'Burnt-out bulbs in the tracking sensor and RPM adjustment',
      'Worn servo and sledge motors',
      'Aged reservoir capacitors',
      'Broken pickup mounts and worn keypads'
    ],
    leadWork: [
      'beogram-400x-relay-replacements',
      'replacing-the-light-bulb-tracking-sensor-on-the-bg4002',
      'replacing-the-incandescent-light-bulbs',
      'speed-testing-with-spindex'
    ]
  }, {
    slug: 'beogram-7000',
    prefix: 'beogram',
    id: '7000',
    path: '/beogram/7000',
    name: 'BeoGram 7000 / 59xx',
    image: cmsImage('e970c043-9670-4c70-9fa2-20747fecceb6.png'),
    imageAlt: 'A restored Bang & Olufsen BeoGram 7000 with its lid open',
    summary: 'The BeoGram 7000 and its 59xx siblings. New electronics, belts and lubrication, and a full adjustment.',
    intro: `The BeoGram 7000 and its 59xx siblings are later tangential turntables. Restoring one means replacing the
      aged electronic components and adjusting the turntable back to specification.`,
    commonIssues: [
      'Aged electronic components',
      'Turntable adjustment drifting out of specification'
    ],
    leadWork: [
      'restoring-the-beogram-7000-changing-the-electronic-components',
      'restoring-the-beogram-7000-adjusting-the-turntable'
    ]
  }, {
    slug: 'beolink-7000',
    prefix: 'beolink',
    id: '7000',
    path: '/beolink/7000',
    name: 'BeoLink 7000',
    image: cmsImage('a5e2d2c3-4ad9-424c-aecd-951e1f678544.jpg'),
    imageAlt: 'A Bang & Olufsen BeoLink 7000 taken apart, with its keypad, display and main PCB',
    summary: 'The BeoLink 7000 remote and its chargers. Backlights, batteries, piezo elements, top plates and more.',
    intro: `The BeoLink 7000 remote and its chargers age in predictable ways: the backlight fades, the batteries
      die, the glass front comes loose and the top plate gets scratched. All of it can be repaired.`,
    commonIssues: [
      'Faded or failing backlight',
      'Failing two way communication',
      'Dead main and CMOS batteries',
      'Failed piezo element',
      'Glass front coming loose',
      'Scratched or dented top plate (#9001)',
      'Chargers that no longer charge'
    ],
    leadWork: [
      'replacing-the-backlight-on-a-beolink-7000',
      'replacing-the-cmos-battery-on-the-beolink-7000',
      'cleaning-and-lubricating-the-beolink-7000',
      'testing-the-beolink-7000'
    ]
  }
];

export const getFocusModel = (prefix: string, id: string): IFocusModel | undefined => {
  return focusModels.find((model: IFocusModel) => model.prefix === prefix && model.id === id);
};

export const getFocusModelBySlug = (slug?: string): IFocusModel | undefined => {
  return focusModels.find((model: IFocusModel) => model.slug === slug);
};
